<template>
    <button type="button" class="kanban-card" :aria-label="descricaoAcessivel" @click="$emit('abrir', contrato)">
        <div class="kanban-card__topo">
            <span class="kanban-card__id">#{{ contrato.id_primario || contrato.id }}</span>
            <Badge
                v-if="contrato.data_vencimento"
                :value="Vencimento.rotulo(contrato.data_vencimento)"
                :severity="Vencimento.severidade(contrato.data_vencimento)"
                :title="'Vence em: ' + Vencimento.data(contrato.data_vencimento)"
            />
        </div>

        <span class="kanban-card__cliente">{{ contrato.cliente || 'Sem cliente' }}</span>

        <span v-if="local" class="kanban-card__local">{{ local }}</span>

        <div v-if="contrato.tenant || subEtapa" class="kanban-card__rodape">
            <!-- Rótulo de empresa: só existe quando o backend manda `tenant`, que
                 é exatamente o modo "Todos". Em empresa única o objeto vem
                 omitido e o card não ganha ruído. Sem ele, a visão consolidada
                 repete o defeito que o multitenant veio corrigir: grade de uma
                 empresa com o cabeçalho de outra. -->
            <span v-if="contrato.tenant" class="kanban-card__empresa">
                <i class="pi pi-building" aria-hidden="true"></i>{{ contrato.tenant.nome }}
            </span>
            <span v-if="subEtapa" class="kanban-card__etapa">{{ contrato.etapa_descricao }}</span>
        </div>
        <!-- ponytail: sem badge de pendências aqui. O payload do Kanban é
             `Contrato::busca()` cru, e o contador de pendências/evidências só
             existe no `ContratoCtrl::list`. Renderizar `contrato.pendencias`
             seria v-if que nunca dispara. -->

    </button>
</template>

<script>
import Badge from 'primevue/badge';
import Vencimento from '@/utils/Vencimento';

export default {
    name: 'KanbanCard',
    components: { Badge },
    props: {
        contrato: { type: Object, required: true },
        coluna: { type: String, required: true },
    },
    emits: ['abrir'],
    data() {
        return { Vencimento };
    },
    computed: {
        // A coluna `cotacao` agrupa duas etapas. Sem isto, "Aguardando
        // Aprovação" ficaria indistinguível de "Cotação" dentro da coluna.
        subEtapa() {
            return this.contrato.etapa !== this.coluna && this.contrato.etapa_descricao;
        },
        local() {
            const cidade = this.contrato.cidade
                ? `${this.contrato.cidade}${this.contrato.uf ? '/' + this.contrato.uf : ''}`
                : '';
            return [this.contrato.site, cidade].filter(Boolean).join(' · ');
        },
        descricaoAcessivel() {
            const partes = [
                `Contrato ${this.contrato.id_primario || this.contrato.id}`,
                this.contrato.cliente,
                this.contrato.tenant ? `empresa ${this.contrato.tenant.nome}` : null,
                this.contrato.data_vencimento
                    ? `vencimento ${Vencimento.data(this.contrato.data_vencimento)}`
                    : null,
            ];
            return `${partes.filter(Boolean).join(', ')}. Abrir contrato`;
        },
    },
};
</script>

<style scoped>
.kanban-card {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    padding: 8px 10px;
    text-align: left;
    background: var(--kanban-card-bg, var(--surface-card));
    border: 1px solid var(--surface-border);
    /* A cor vem da coluna (herdada por custom property, como os fundos acima):
       é a etapa que define a lateral, e o card não precisa conhecer a paleta.
       O fallback é o estado antigo, para o caso de um card fora de coluna. */
    border-left: 3px solid var(--kanban-card-borda, var(--primary-color));
    border-radius: var(--border-radius);
    cursor: pointer;
    font-family: inherit;
    color: var(--p-text-color, var(--surface-900));
    /* Só `border-color` transiciona. Com `background-color` na lista, o card
       ficava segundos com a cor do tema anterior depois da troca claro/escuro:
       o valor vem de custom property herdada, e o Chrome não reavalia o `var()`
       do fundo enquanto há transição registrada nele — medido em 5s de atraso,
       com a coluna (que não transiciona) já repintada. */
    transition: border-color 140ms ease;
}
.kanban-card:hover {
    background: var(--kanban-card-hover-bg, var(--surface-hover));
    border-color: var(--surface-400);
}
/* O foco global do tema está zerado (`--p-focus-ring-*` em app.css). Numa
   coluna rolável, navegar por teclado sem indicador é navegar às cegas. */
.kanban-card:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 1px;
}
.kanban-card__topo,
.kanban-card__rodape {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    flex-wrap: wrap;
}
.kanban-card__id {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--surface-600);
}
.kanban-card__cliente {
    font-size: 0.85rem;
    font-weight: 600;
    line-height: 1.2;
    overflow-wrap: anywhere;
}
.kanban-card__local {
    font-size: 0.75rem;
    color: var(--surface-600);
    overflow-wrap: anywhere;
}
.kanban-card__empresa,
.kanban-card__etapa {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    padding: 1px 6px;
    border-radius: 10px;
    font-size: 0.7rem;
    line-height: 1.5;
    overflow-wrap: anywhere;
}
.kanban-card__empresa {
    background: var(--surface-200);
    color: var(--surface-800);
    font-weight: 600;
}
.kanban-card__etapa {
    background: transparent;
    border: 1px dashed var(--surface-400);
    color: var(--surface-700);
}
</style>
